import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import {
  Sparkles,
  Calendar as CalendarIcon,
  Clock,
  Users,
  TrendingUp,
  Plus,
  X,
  Save,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Check,
  Send,
  Loader,
  Gift,
  Heart,
  Briefcase,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { contactService } from '../services/api';
import { useToast, ToastContainer } from '../componets/Toast';
import API_URL from '../config/API_URL';
import PaywallModal from '../subscription/PaywallModal';
const localizer = momentLocalizer(moment);

const NetworkingCalendar = () => {
  const [events, setEvents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [addEventModalOpen, setAddEventModalOpen] = useState(false);
  const [aiSuggestionsOpen, setAiSuggestionsOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [findConnectionsOpen, setFindConnectionsOpen] = useState(false);
  const [connectionSuggestions, setConnectionSuggestions] = useState([]);
  const [addingConnection, setAddingConnection] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const chatEndRef = useRef(null);

  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeatureInfo, setPaywallFeatureInfo] = useState(null);

  const { toasts, removeToast, showSuccess, showError, showInfo } = useToast();

  // New event form state
  const [newEvent, setNewEvent] = useState({
    title: '',
    contact_id: '',
    event_type: 'call',
    start_date: '',
    start_time: '',
    duration: 30,
    notes: '',
    reminder: true,
  });

  const styles = {
    page: {
      padding: '2rem',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    header: {
      marginBottom: '2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    headerLeft: {
      flex: 1,
    },
    title: {
      fontSize: '2rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    subtitle: {
      color: '#475569',
      fontSize: '1rem',
      marginBottom: '1rem',
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.25rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
    },
    primaryButton: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    },
    secondaryButton: {
      background: '#ffffff',
      color: '#667eea',
      border: '2px solid #e2e8f0',
    },
    calendarCard: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '2rem',
      minHeight: '600px',
    },
    legendContainer: {
      display: 'flex',
      gap: '1.5rem',
      marginBottom: '1.5rem',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '12px',
      flexWrap: 'wrap',
    },
    legendTitle: {
      fontSize: '0.85rem',
      fontWeight: 600,
      color: '#475569',
      marginRight: '0.5rem',
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.85rem',
      color: '#64748b',
    },
    legendDot: {
      width: '12px',
      height: '12px',
      borderRadius: '3px',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem',
    },
    modalContent: {
      background: 'white',
      borderRadius: '16px',
      padding: '2rem',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      position: 'relative',
    },
    chatModalContent: {
      background: 'white',
      borderRadius: '16px',
      maxWidth: '800px',
      width: '100%',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      paddingBottom: '1rem',
      borderBottom: '1px solid #e2e8f0',
    },
    chatModalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1.5rem 2rem',
      borderBottom: '1px solid #e2e8f0',
    },
    modalTitle: {
      fontSize: '1.5rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#64748b',
      padding: '0.5rem',
      borderRadius: '8px',
      transition: 'all 200ms',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#0f172a',
      fontWeight: 500,
      fontSize: '0.95rem',
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      transition: 'all 200ms',
    },
    select: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      cursor: 'pointer',
    },
    textarea: {
      width: '95%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    eventDetails: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    eventDetailRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      color: '#475569',
    },
    sourceTag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.75rem',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '0.5rem',
    },
    importantDateTag: {
      background: '#fce7f3',
      color: '#be123c',
    },
    eventTag: {
      background: '#dbeafe',
      color: '#1e40af',
    },
    suggestionCard: {
      background: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      padding: '1.25rem',
      marginBottom: '1rem',
      cursor: 'pointer',
      transition: 'all 200ms',
    },
    suggestionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '0.75rem',
    },
    suggestionTitle: {
      fontSize: '1.05rem',
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '0.25rem',
    },
    suggestionReason: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginBottom: '0.5rem',
    },
    suggestionMeta: {
      display: 'flex',
      gap: '1rem',
      fontSize: '0.85rem',
      color: '#64748b',
    },
    priorityBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.25rem 0.75rem',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem',
      color: '#64748b',
    },
    chatContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '2rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    chatMessage: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      maxWidth: '80%',
    },
    userMessage: {
      alignSelf: 'flex-end',
      alignItems: 'flex-end',
    },
    assistantMessage: {
      alignSelf: 'flex-start',
      alignItems: 'flex-start',
    },
    messageBubble: {
      padding: '1rem 1.25rem',
      borderRadius: '16px',
      fontSize: '0.95rem',
      lineHeight: 1.5,
    },
    userBubble: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderBottomRightRadius: '4px',
    },
    assistantBubble: {
      background: '#f8fafc',
      color: '#0f172a',
      border: '1px solid #e2e8f0',
      borderBottomLeftRadius: '4px',
    },
    chatInputContainer: {
      padding: '1.5rem 2rem',
      borderTop: '1px solid #e2e8f0',
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'flex-end',
    },
    chatInput: {
      flex: 1,
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      resize: 'none',
      maxHeight: '120px',
      minHeight: '44px',
    },
    sendButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.875rem 1.25rem',
      border: 'none',
      borderRadius: '12px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      cursor: 'pointer',
      transition: 'all 200ms',
      fontWeight: 600,
    },
    connectionResultsContainer: {
      marginTop: '1.5rem',
      padding: '1.5rem',
      background: '#ffffff',
      borderRadius: '12px',
      border: '2px solid #e2e8f0',
    },
    resultsHeader: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
  };

  useEffect(() => {
    loadContacts();
    loadEvents();
  }, []);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  useEffect(() => {
    if (findConnectionsOpen && chatMessages.length === 0) {
      setChatMessages([
        {
          role: 'assistant',
          content:
            "Hi! I'm here to help you find valuable connections on LinkedIn. You can give me specific criteria like:\n\n• Industry or role (e.g., 'software engineers')\n• Location (e.g., 'in Boston')\n• Company (e.g., 'at Google')\n• School (e.g., 'from MIT')\n• Interests (e.g., 'interested in AI')\n\nOr just tell me what you're looking for, and I'll find the best matches!",
        },
      ]);
    }
  }, [findConnectionsOpen]);

  // ✨ POST-PURCHASE RESTORATION EFFECT
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromPayment = urlParams.get('from_payment');
    
    // ✨ READ FROM LOCALSTORAGE (persists across navigation)
    const savedQuery = localStorage.getItem('linkedin_search_query');
    const isPending = localStorage.getItem('linkedin_search_pending');
    
    console.log('🔍 Post-payment check:', { fromPayment, savedQuery, isPending });
    
    if (fromPayment === 'linkedin_search' && savedQuery && isPending === 'true') {
      console.log('✅ Restoring search:', savedQuery);
      
      // Clear localStorage first
      localStorage.removeItem('linkedin_search_query');
      localStorage.removeItem('linkedin_search_pending');
      
      // Clear URL parameter
      window.history.replaceState({}, '', '/dashboard/calendar');
      
      // Reopen modal with saved query
      setFindConnectionsOpen(true);
      setUserInput(savedQuery);
      
      // Show success message
      showSuccess('Credits added! Searching for connections now...');
      
      // Auto-submit after delay to ensure modal is open
      setTimeout(() => {
        console.log('🚀 Auto-submitting search');
        handleSendMessage();
      }, 800);
    }
  }, []); // Empty array - only run once on mount

  const loadContacts = async () => {
    try {
      const data = await contactService.getContacts();
      setContacts(data.contacts);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      showError('Failed to load contacts');
    }
  };

  const loadEvents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/upcoming-all?days_ahead=365`, {
        credentials: 'include',
      });
      const data = await response.json();

      // Transform both types for react-big-calendar
      const transformedEvents = data.upcoming.map((item) => {
        const startDate = new Date(item.date_value);
        
        // For networking events with specific times
        if (item.source === 'networking_event' && item.start_time) {
          return {
            ...item,
            start: new Date(item.start_time),
            end: item.end_time ? new Date(item.end_time) : new Date(new Date(item.start_time).getTime() + 30 * 60000),
          };
        }
        
        // For important dates (all-day events)
        return {
          ...item,
          start: startDate,
          end: startDate,
          allDay: true,
        };
      });

      setEvents(transformedEvents);
    } catch (error) {
      console.error('Failed to load events:', error);
      showError('Failed to load calendar');
    }
  };

  const loadAISuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await fetch(`${API_URL}/api/calendar/ai-suggestions`, {
        credentials: 'include',
      });
      const data = await response.json();
      setAiSuggestions(data.suggestions);
      setAiSuggestionsOpen(true);
    } catch (error) {
      console.error('Failed to load AI suggestions:', error);
      showError('Failed to generate suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSendMessage = async () => {
    if (!userInput.trim() || isGenerating) return;

    const userMessage = userInput.trim();
    
    // Check feature access BEFORE processing
    try {
      const accessResponse = await fetch(`${API_URL}/api/subscription/check-access/linkedin_search`, {
        credentials: 'include',
      });
      
      const accessData = await accessResponse.json();
      
      if (!accessData.has_access) {
        // ✨ SAVE TO LOCALSTORAGE (persists across navigation)
        localStorage.setItem('linkedin_search_query', userMessage);
        localStorage.setItem('linkedin_search_pending', 'true');
        
        // Show paywall
        setPaywallFeatureInfo(accessData);
        setShowPaywall(true);
        return;
      }
      
      // Has access, proceed with search
      setUserInput('');
      setChatMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
      setIsGenerating(true);

      const conversationHistory = chatMessages
        .map((msg) => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
        .join('\n');

      const response = await fetch(`${API_URL}/api/calendar/find-connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          query: userMessage,
          conversationHistory: conversationHistory,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setChatMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: data.response || 'Here are the connections I found:',
            suggestions: data.suggestions || [],
          },
        ]);
        setConnectionSuggestions(data.suggestions || []);
      } else {
        showError('Failed to find connections');
        setChatMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try rephrasing your request.',
          },
        ]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      
      if (error.message && error.message.includes('feature_access_required')) {
        // ✨ SAVE TO LOCALSTORAGE
        localStorage.setItem('linkedin_search_query', userMessage);
        localStorage.setItem('linkedin_search_pending', 'true');
        
        setPaywallFeatureInfo(error);
        setShowPaywall(true);
      } else {
        showError('Failed to process request');
        setChatMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
          },
        ]);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedEvent(null);
  }, []);

  const handleAddEvent = useCallback(() => {
    setAddEventModalOpen(true);
  }, []);

  const handleNavigate = useCallback((newDate) => {
    setDate(newDate);
  }, []);

  const handleSaveEvent = async () => {
    if (!newEvent.title || !newEvent.contact_id || !newEvent.start_date) {
      showError('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/calendar/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newEvent),
      });

      if (response.ok) {
        showSuccess('Event created successfully!');
        setAddEventModalOpen(false);
        setNewEvent({
          title: '',
          contact_id: '',
          event_type: 'call',
          start_date: '',
          start_time: '',
          duration: 30,
          notes: '',
          reminder: true,
        });
        loadEvents();
      } else {
        showError('Failed to create event');
      }
    } catch (error) {
      console.error('Failed to save event:', error);
      showError('Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion) => {
    setNewEvent({
      title: suggestion.suggested_title,
      contact_id: suggestion.contact.id,
      event_type: suggestion.event_type,
      start_date: suggestion.suggested_date,
      start_time: suggestion.suggested_time || '14:00',
      duration: 30,
      notes: suggestion.reason,
      reminder: true,
    });
    setAiSuggestionsOpen(false);
    setAddEventModalOpen(true);
  };

  const handleSelectConnection = async (connection) => {
    setAddingConnection(true);
    try {
      const response = await fetch(`${API_URL}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: connection.name,
          company: connection.company,
          role: connection.current_role,
          linkedin_url: connection.linkedin_url,
          connection_origin: 'AI Recommended Connection',
          conversation_notes: connection.common_ground,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showSuccess(`${connection.name} added to contacts!`);
        await loadContacts();

        setNewEvent({
          title: `Initial outreach to ${connection.name}`,
          contact_id: data.contact.id,
          event_type: 'coffee',
          start_date: moment().add(3, 'days').format('YYYY-MM-DD'),
          start_time: '14:00',
          duration: 30,
          notes: connection.reasoning,
          reminder: true,
        });

        setFindConnectionsOpen(false);
        setChatMessages([]);
        setConnectionSuggestions([]);
        setAddEventModalOpen(true);
      } else {
        showError('Failed to add connection');
      }
    } catch (error) {
      showError('Failed to add connection');
      console.error(error);
    } finally {
      setAddingConnection(false);
    }
  };

  const handleCloseFindConnections = () => {
    setFindConnectionsOpen(false);
    setChatMessages([]);
    setConnectionSuggestions([]);
    setUserInput('');
  };

  const eventStyleGetter = (event) => {
    // Important Dates - Pink/Red tones
    if (event.source === 'important_date') {
      const importantDateColors = {
        birthday: { bg: '#ec4899', border: '#be123c' },
        anniversary: { bg: '#ef4444', border: '#dc2626' },
        work_anniversary: { bg: '#a855f7', border: '#7e22ce' },
        custom: { bg: '#f472b6', border: '#db2777' },
      };
      
      const color = importantDateColors[event.type] || importantDateColors.custom;
      
      return {
        style: {
          backgroundColor: color.bg,
          borderLeft: `4px solid ${color.border}`,
          borderRadius: '6px',
          color: 'white',
          border: 'none',
          padding: '4px 8px',
          fontSize: '0.85rem',
          fontWeight: 600,
        },
      };
    }

    // Networking Events - Blue/Green tones
    if (event.source === 'networking_event') {
      const networkingEventColors = {
        call: { bg: '#667eea', border: '#5a67d8' },
        meeting: { bg: '#10b981', border: '#059669' },
        coffee: { bg: '#f59e0b', border: '#d97706' },
        followup: { bg: '#3b82f6', border: '#2563eb' },
        other: { bg: '#64748b', border: '#475569' },
      };

      const color = networkingEventColors[event.type] || networkingEventColors.other;

      return {
        style: {
          backgroundColor: color.bg,
          borderLeft: `4px solid ${color.border}`,
          borderRadius: '6px',
          color: 'white',
          border: 'none',
          padding: '4px 8px',
          fontSize: '0.85rem',
          fontWeight: 500,
        },
      };
    }

    // Default
    return {
      style: {
        backgroundColor: '#64748b',
        borderRadius: '6px',
        color: 'white',
        border: 'none',
        padding: '4px 8px',
        fontSize: '0.85rem',
        fontWeight: 500,
      },
    };
  };

  const customToolbar = useCallback(
    (toolbar) => {
      const goToBack = () => toolbar.onNavigate('PREV');
      const goToNext = () => toolbar.onNavigate('NEXT');
      const goToToday = () => toolbar.onNavigate('TODAY');
      const label = toolbar.label;

      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            padding: '1rem',
            background: '#f8fafc',
            borderRadius: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h3
              style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#0f172a',
                fontFamily: "'Syne', sans-serif",
              }}
            >
              {label}
            </h3>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={goToBack}
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                padding: '0.5rem 0.75rem',
              }}
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={goToToday}
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                padding: '0.5rem 1rem',
              }}
            >
              Today
            </button>
            <button
              onClick={goToNext}
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                padding: '0.5rem 0.75rem',
              }}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      );
    },
    []
  );

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return { bg: '#fee2e2', text: '#dc2626' };
      case 'medium':
        return { bg: '#fef3c7', text: '#d97706' };
      case 'low':
        return { bg: '#dbeafe', text: '#2563eb' };
      default:
        return { bg: '#f1f5f9', text: '#64748b' };
    }
  };

  return (
    <div style={styles.page}>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => {
          setShowPaywall(false);
          // ✨ If they close without buying, restore from localStorage
          const savedQuery = localStorage.getItem('linkedin_search_query');
          if (savedQuery) {
            setUserInput(savedQuery);
            // Keep it in localStorage in case they try again
          }
        }}
        featureInfo={paywallFeatureInfo}
        sourcePage="calendar"  // ✅ ADD THIS LINE!!!
        onPurchaseComplete={() => {
          setShowPaywall(false);
          // Search will happen automatically in useEffect after redirect
        }}
      />

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>Networking Calendar</h1>
          <p style={styles.subtitle}>AI-powered scheduling to keep your network engaged</p>
        </div>
        <div style={styles.headerActions}>
          <button
            onClick={loadAISuggestions}
            disabled={loadingSuggestions}
            style={{
              ...styles.button,
              ...styles.secondaryButton,
            }}
          >
            <Sparkles size={18} />
            {loadingSuggestions ? 'Loading...' : 'AI Suggestions'}
          </button>

          <button
            onClick={() => setFindConnectionsOpen(true)}
            style={{
              ...styles.button,
              ...styles.secondaryButton,
            }}
          >
            <Users size={18} />
            Find Connections
          </button>

          <button
            onClick={handleAddEvent}
            style={{
              ...styles.button,
              ...styles.primaryButton,
            }}
          >
            <Plus size={18} />
            Add Event
          </button>
        </div>
      </div>

      <div style={styles.calendarCard}>
        {/* Legend */}
        <div style={styles.legendContainer}>
          <span style={styles.legendTitle}>Legend:</span>
          
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#ec4899' }} />
            <span>Birthdays</span>
          </div>
          
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#ef4444' }} />
            <span>Anniversaries</span>
          </div>
          
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#a855f7' }} />
            <span>Work Anniversaries</span>
          </div>
          
          <div style={{ width: '1px', height: '20px', background: '#e2e8f0' }} />
          
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#667eea' }} />
            <span>Calls</span>
          </div>
          
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#10b981' }} />
            <span>Meetings</span>
          </div>
          
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#f59e0b' }} />
            <span>Coffee Chats</span>
          </div>
          
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#3b82f6' }} />
            <span>Follow-ups</span>
          </div>
        </div>

        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '600px' }}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={handleSelectEvent}
          components={{
            toolbar: customToolbar,
          }}
          views={['month', 'week', 'day', 'agenda']}
          defaultView="month"
          date={date}
          onNavigate={handleNavigate}
          popup
          selectable
        />
      </div>

      {/* Event Details Modal */}
      <AnimatePresence>
        {modalOpen && selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.modal}
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={styles.modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <div>
                  <span style={{
                    ...styles.sourceTag,
                    ...(selectedEvent.source === 'important_date' ? styles.importantDateTag : styles.eventTag),
                  }}>
                    {selectedEvent.source === 'important_date' ? '📅 Important Date' : '🗓️ Scheduled Event'}
                  </span>
                  <h3 style={styles.modalTitle}>{selectedEvent.title}</h3>
                </div>
                <button
                  onClick={handleCloseModal}
                  style={styles.closeButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={styles.eventDetails}>
                <div style={styles.eventDetailRow}>
                  <CalendarIcon size={18} />
                  <span>{moment(selectedEvent.start).format('MMMM D, YYYY')}</span>
                </div>
                {!selectedEvent.allDay && (
                  <div style={styles.eventDetailRow}>
                    <Clock size={18} />
                    <span>
                      {moment(selectedEvent.start).format('h:mm A')} -{' '}
                      {moment(selectedEvent.end).format('h:mm A')}
                    </span>
                  </div>
                )}
                {selectedEvent.contact_name && (
                  <div style={styles.eventDetailRow}>
                    <Users size={18} />
                    <span>{selectedEvent.contact_name}</span>
                  </div>
                )}
                {selectedEvent.notes && (
                  <div style={{ marginTop: '1rem' }}>
                    <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Notes:</strong>
                    <p style={{ color: '#475569', lineHeight: 1.6 }}>{selectedEvent.notes}</p>
                  </div>
                )}
                {selectedEvent.recurring && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: '#eff6ff',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    color: '#1e40af',
                  }}>
                    📅 This is a recurring annual event
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Event Modal */}
      <AnimatePresence>
        {addEventModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.modal}
            onClick={() => setAddEventModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={styles.modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>Add Networking Event</h3>
                <button
                  onClick={() => setAddEventModalOpen(false)}
                  style={styles.closeButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Event Title *</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  placeholder="e.g., Coffee chat with Sarah"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Contact *</label>
                <select
                  value={newEvent.contact_id}
                  onChange={(e) => setNewEvent({ ...newEvent, contact_id: e.target.value })}
                  style={styles.select}
                >
                  <option value="">Select contact...</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.name}
                      {contact.company && ` - ${contact.company}`}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Event Type</label>
                <select
                  value={newEvent.event_type}
                  onChange={(e) => setNewEvent({ ...newEvent, event_type: e.target.value })}
                  style={styles.select}
                >
                  <option value="call">Phone Call</option>
                  <option value="meeting">Meeting</option>
                  <option value="coffee">Coffee Chat</option>
                  <option value="followup">Follow-up</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr',
                  gap: '1rem',
                }}
              >
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date *</label>
                  <input
                    type="date"
                    value={newEvent.start_date}
                    onChange={(e) => setNewEvent({ ...newEvent, start_date: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Time</label>
                  <input
                    type="time"
                    value={newEvent.start_time}
                    onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Duration (min)</label>
                  <input
                    type="number"
                    value={newEvent.duration}
                    onChange={(e) =>
                      setNewEvent({
                        ...newEvent,
                        duration: parseInt(e.target.value),
                      })
                    }
                    min="15"
                    step="15"
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes / Agenda</label>
                <textarea
                  value={newEvent.notes}
                  onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                  placeholder="What do you want to discuss?"
                  style={styles.textarea}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button
                  onClick={() => setAddEventModalOpen(false)}
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton,
                    flex: 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEvent}
                  disabled={loading}
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    flex: 1,
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  <Save size={18} />
                  {loading ? 'Saving...' : 'Save Event'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Suggestions Panel */}
      <AnimatePresence>
        {aiSuggestionsOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.modal}
            onClick={() => setAiSuggestionsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={styles.modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <div>
                  <h3 style={styles.modalTitle}>AI Networking Suggestions</h3>
                  <p style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '0.25rem' }}>
                    Smart recommendations based on your contact activity
                  </p>
                </div>
                <button
                  onClick={() => setAiSuggestionsOpen(false)}
                  style={styles.closeButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {aiSuggestions.length === 0 ? (
                <div style={styles.emptyState}>
                  <Lightbulb size={48} style={{ color: '#cbd5e1', marginBottom: '0.5rem' }} />
                  <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                    No suggestions at the moment
                  </p>
                  <p style={{ fontSize: '0.9rem' }}>
                    Keep adding contacts and interactions to get personalized recommendations
                  </p>
                </div>
              ) : (
                <div>
                  {aiSuggestions.map((suggestion, idx) => {
                    const priorityColor = getPriorityColor(suggestion.priority);
                    return (
                      <div
                        key={idx}
                        style={styles.suggestionCard}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#cbd5e1';
                          e.currentTarget.style.background = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.background = '#f8fafc';
                        }}
                      >
                        <div style={styles.suggestionHeader}>
                          <div style={{ flex: 1 }}>
                            <div style={styles.suggestionTitle}>{suggestion.contact.name}</div>
                            <div style={styles.suggestionReason}>{suggestion.reason}</div>
                          </div>
                          <span
                            style={{
                              ...styles.priorityBadge,
                              background: priorityColor.bg,
                              color: priorityColor.text,
                            }}
                          >
                            {suggestion.priority}
                          </span>
                        </div>
                        <div style={styles.suggestionMeta}>
                          <span>
                            <strong>Last contact:</strong>{' '}
                            {moment(suggestion.last_contact_date).fromNow()}
                          </span>
                          <span>
                            <strong>Suggested:</strong> {suggestion.event_type}
                          </span>
                        </div>
                        <button
                          onClick={() => handleAcceptSuggestion(suggestion)}
                          style={{
                            ...styles.button,
                            ...styles.primaryButton,
                            marginTop: '1rem',
                            width: '100%',
                          }}
                        >
                          <Check size={16} />
                          Schedule Event
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Find Connections Chat Panel */}
      <AnimatePresence>
        {findConnectionsOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.modal}
            onClick={handleCloseFindConnections}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={styles.chatModalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.chatModalHeader}>
                <div>
                  <h3 style={styles.modalTitle}>Find LinkedIn Connections</h3>
                  <p style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '0.25rem' }}>
                    Tell me what kind of connections you're looking for
                  </p>
                </div>
                <button
                  onClick={handleCloseFindConnections}
                  style={styles.closeButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={styles.chatContainer}>
                {chatMessages.map((message, idx) => (
                  <div
                    key={idx}
                    style={{
                      ...styles.chatMessage,
                      ...(message.role === 'user' ? styles.userMessage : styles.assistantMessage),
                    }}
                  >
                    <div
                      style={{
                        ...styles.messageBubble,
                        ...(message.role === 'user' ? styles.userBubble : styles.assistantBubble),
                      }}
                    >
                      {message.content}
                    </div>

                    {message.suggestions && message.suggestions.length > 0 && (
                      <div style={styles.connectionResultsContainer}>
                        <div style={styles.resultsHeader}>
                          <Users size={18} />
                          Found {message.suggestions.length} connection
                          {message.suggestions.length !== 1 ? 's' : ''}
                        </div>
                        {message.suggestions.map((suggestion, sIdx) => {
                          const priorityColor = getPriorityColor(suggestion.priority);
                          return (
                            <div
                              key={sIdx}
                              style={styles.suggestionCard}
                              onClick={() => handleSelectConnection(suggestion)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#cbd5e1';
                                e.currentTarget.style.background = '#ffffff';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#e2e8f0';
                                e.currentTarget.style.background = '#f8fafc';
                              }}
                            >
                              <div style={styles.suggestionHeader}>
                                <div style={{ flex: 1 }}>
                                  <div style={styles.suggestionTitle}>{suggestion.name}</div>
                                  <div style={styles.suggestionReason}>{suggestion.reasoning}</div>
                                </div>
                                <span
                                  style={{
                                    ...styles.priorityBadge,
                                    background: priorityColor.bg,
                                    color: priorityColor.text,
                                  }}
                                >
                                  {suggestion.priority}
                                </span>
                              </div>
                              <div
                                style={{
                                  marginTop: '0.75rem',
                                  fontSize: '0.85rem',
                                  color: '#64748b',
                                }}
                              >
                                <div>
                                  <strong>Role:</strong> {suggestion.current_role} at{' '}
                                  {suggestion.company}
                                </div>
                                <div style={{ marginTop: '0.25rem' }}>
                                  <strong>Location:</strong> {suggestion.location}
                                </div>
                                {suggestion.common_ground && (
                                  <div style={{ marginTop: '0.25rem' }}>
                                    <strong>Common Ground:</strong> {suggestion.common_ground}
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  marginTop: '1rem',
                                  padding: '0.75rem',
                                  background: '#eff6ff',
                                  borderRadius: '8px',
                                  fontSize: '0.85rem',
                                  color: '#1e40af',
                                }}
                              >
                                Click to add as contact and schedule outreach
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}

                {isGenerating && (
                  <div style={{ ...styles.chatMessage, ...styles.assistantMessage }}>
                    <div style={{ ...styles.messageBubble, ...styles.assistantBubble }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: '#667eea',
                        }}
                      >
                        <Loader 
                          size={16} 
                          style={{
                            animation: 'spin 1s linear infinite',
                          }}
                        />
                        Searching for connections...
                      </div>
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              <div style={styles.chatInputContainer}>
                <textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="e.g., 'Find software engineers from MIT' or 'Show me VCs in Boston'"
                  style={styles.chatInput}
                  disabled={isGenerating}
                  rows={1}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!userInput.trim() || isGenerating}
                  style={{
                    ...styles.sendButton,
                    opacity: !userInput.trim() || isGenerating ? 0.5 : 1,
                    cursor: !userInput.trim() || isGenerating ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Send size={18} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NetworkingCalendar;